import React, { useEffect, useState, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const DEFAULT_COLORS = [
  "#4dc9f6",
  "#f67019",
  "#f53794",
  "#537bc4",
  "#acc236",
  "#166a8f",
  "#00a950",
  "#58595b",
  "#8549ba",
  "#ff9f40",
];

export default function CategoriesPieChart({
  apiEndpoint,
  data: propData,
  onSelect, // function(name|null)
  selected, // currently selected slice name (string) or null
  fieldPriority = ["CATEGORIES", "categories", "Cat"], // fields to aggregate from raw rows
}) {
  const [data, setData] = useState(propData || null);
  const [loading, setLoading] = useState(!propData && !!apiEndpoint);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (propData) {
      setData(propData);
      setLoading(false);
      setError(null);
      return;
    }
    if (!apiEndpoint) return;

    let cancelled = false;
    setLoading(true);
    fetch(apiEndpoint)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (cancelled) return;
        setData(json);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, propData]);

  // Normalize to [{ name, value }]
  const counts = useMemo(() => {
    if (!data) return [];

    // Already aggregated shapes
    if (
      Array.isArray(data) &&
      data.length > 0 &&
      data[0] &&
      Object.prototype.hasOwnProperty.call(data[0], "name") &&
      Object.prototype.hasOwnProperty.call(data[0], "value")
    ) {
      return data.map((d) => ({ name: d.name, value: Number(d.value) || 0 }));
    }

    if (
      Array.isArray(data) &&
      data.length > 0 &&
      data[0] &&
      Object.prototype.hasOwnProperty.call(data[0], "label") &&
      Object.prototype.hasOwnProperty.call(data[0], "value")
    ) {
      return data.map((d) => ({ name: d.label, value: Number(d.value) || 0 }));
    }

    // Raw rows aggregation by candidate fields
    if (Array.isArray(data)) {
      const map = new Map();
      data.forEach((row) => {
        let cats = "";
        for (const f of fieldPriority) {
          if (row && row[f]) {
            cats = row[f];
            break;
          }
        }
        if (!cats) return;
        cats
          .toString()
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length)
          .forEach((cat) => {
            const key = cat;
            map.set(key, (map.get(key) || 0) + 1);
          });
      });
      return Array.from(map.entries()).map(([name, value]) => ({
        name,
        value,
      }));
    }

    return [];
  }, [data, fieldPriority]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>Error: {error}</div>;
  if (!counts.length) return <div>No category data found.</div>;

  const colors = counts.map(
    (_, i) => DEFAULT_COLORS[i % DEFAULT_COLORS.length]
  );

  function handleSliceClick(name) {
    if (!onSelect) return;
    // toggle: if same selected -> clear
    if (selected === name) onSelect(null);
    else onSelect(name);
  }

  return (
    <div style={{ width: "100%", height: 400 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={counts}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={120}
            innerRadius={40}
            label={(entry) => `${entry.name} (${entry.value})`}
          >
            {counts.map((entry, index) => {
              const isSelected = selected === entry.name;
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={colors[index]}
                  stroke={isSelected ? "#000" : undefined}
                  strokeWidth={isSelected ? 3 : 1}
                  cursor="pointer"
                  onClick={() => handleSliceClick(entry.name)}
                />
              );
            })}
          </Pie>
          <Tooltip />
          <Legend verticalAlign="bottom" height={36} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
